import React, { useState, useEffect } from 'react';
import { X, Phone, Check, MessageCircle, Smartphone, Loader2 } from 'lucide-react';
import { api } from '../../utils/api';
import WhatsAppPreferences from './WhatsAppPreferences';
import './PhoneSettings.css';

// Basic phone number validation (E.164-ish)
function isValidPhone(phone) {
  const cleaned = phone.replace(/[\s\-()]/g, '');
  return /^\+\d{10,15}$/.test(cleaned) || /^\d{10}$/.test(cleaned);
}

// Format phone number with dashes: +15551234567 → +1-555-123-4567
function formatPhoneDisplay(phone) {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

function PhoneSettings({ isOpen, onClose, userPhone, phoneVerified, onPhoneUpdated }) {
  const [step, setStep] = useState('enter');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // WhatsApp configuration from backend
  const [whatsappNumber, setWhatsappNumber] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Fetch WhatsApp status on mount and determine initial step
  useEffect(() => {
    if (isOpen) {
      setConfigLoading(true);
      api.getWhatsAppStatus()
        .then((status) => {
          setWhatsappNumber(status.whatsapp_number || null);
        })
        .catch(() => {})
        .finally(() => setConfigLoading(false));

      // If phone already linked, go straight to preferences
      if (userPhone && phoneVerified) {
        setStep('preferences');
      } else {
        setStep('enter');
      }
    }
  }, [isOpen, userPhone, phoneVerified]);

  if (!isOpen) return null;

  const handleLinkPhone = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValidPhone(phoneNumber)) {
      setError('Please enter a valid phone number with country code (e.g., +1 555 123 4567).');
      return;
    }

    setLoading(true);

    try {
      const response = await api.linkPhone(phoneNumber);
      if (response.whatsapp_number) {
        setWhatsappNumber(response.whatsapp_number);
      }
      setStep('linked');
      setSuccess('Phone number linked successfully!');
      if (onPhoneUpdated) {
        onPhoneUpdated(phoneNumber, true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setPhoneNumber('');
    onClose();
  };

  // Build WhatsApp deep link with a simple greeting
  const botNumber = whatsappNumber ? whatsappNumber.replace(/\D/g, '') : '';
  const waLink = botNumber
    ? `https://wa.me/${botNumber}?text=${encodeURIComponent('Hi')}`
    : '';

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose} aria-label="Close settings">
          <X size={20} />
        </button>

        <div className="modal-header">
          <MessageCircle size={32} className="modal-icon" />
          <h2>WhatsApp Integration</h2>
          <p>Chat with your AI financial assistant via WhatsApp</p>
        </div>

        {configLoading && (
          <div className="config-loading">
            <Loader2 size={20} className="spinner" />
            <span>Loading WhatsApp configuration...</span>
          </div>
        )}

        {error && <div className="settings-error">{error}</div>}
        {success && <div className="settings-success">{success}</div>}

        {/* ===== PHONE ALREADY LINKED - SHOW PREFERENCES ===== */}
        {step === 'preferences' && !configLoading && (
          <div className="preferences-step">
            <div className="linked-phone-info">
              <Phone size={16} />
              <span>Connected: <strong>{formatPhoneDisplay(userPhone)}</strong></span>
            </div>
            <WhatsAppPreferences />
            <button
              type="button"
              className="btn-primary"
              onClick={handleClose}
              style={{ marginTop: '16px' }}
            >
              Done
            </button>
          </div>
        )}

        {/* ===== ENTER PHONE NUMBER ===== */}
        {step === 'enter' && !configLoading && (
          <form onSubmit={handleLinkPhone} className="phone-form">
            <div className="setup-instructions">
              <h3>Link your phone number</h3>
              <p>
                Enter your phone number to connect your account with WhatsApp.
              </p>
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 (555) 123-4567"
                required
                disabled={loading}
              />
              <span className="input-hint">Include country code. US numbers default to +1.</span>
            </div>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Linking...' : 'Link Phone Number'}
            </button>
          </form>
        )}

        {/* ===== PHONE JUST LINKED - SUCCESS + PREFERENCES ===== */}
        {step === 'linked' && !configLoading && (
          <div className="preferences-step">
            <div className="linked-phone-info">
              <Check size={16} />
              <span>Connected: <strong>{formatPhoneDisplay(phoneNumber || userPhone)}</strong></span>
            </div>

            <p className="simple-instruction">
              Your phone is linked! Send a message to Kigi on WhatsApp to start chatting.
            </p>

            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp-open"
              >
                <MessageCircle size={18} />
                Open WhatsApp
              </a>
            )}

            <WhatsAppPreferences />

            <button
              type="button"
              className="btn-primary"
              onClick={handleClose}
              style={{ marginTop: '16px' }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhoneSettings;
